/**
 * A workspace's capability flags for the current user, fetched by id.
 *
 * `GET /workspaces/:id/user-membership` is the single source of truth for what
 * someone may do in a workspace, and three places already call it by hand
 * (WorkspaceContainer, BillDetailPage, BudgetItemDetailPage). This hook exists
 * for components that are handed a workspaceId and nothing else — notably
 * ViewNotesModal, which is mounted from 19 places across the app and cannot
 * realistically have a permissions prop threaded to all of them.
 *
 * Deliberately NOT wired into those three existing callers yet: WorkspaceContainer
 * gates every workspace tab, so consolidating them is a wider change than the one
 * that needed this hook. Left for its own PR.
 *
 * `accessType` matters as much as the flags. Several distinct principals surface
 * with role `owner` — an explicit workspace_members owner, a system_admin, an
 * org_admin of the owner org, and an org_admin of an org the workspace is merely
 * shared with — and they do not hold the same rights. Read the capability or the
 * accessType, never the label (ACCESS_CONTROL_MODEL.md §1).
 */
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';

export interface WorkspaceMembershipPermissions {
  role?: string;
  accessType?: string;
  canView: boolean;
  canEdit: boolean;
  canManage: boolean;
  canInvite: boolean;
  canDelete: boolean;
}

/** True for an org_admin of the workspace's OWNER org, or a system_admin. */
export function isOrgAdminOfOwnerOrg(
  permissions?: WorkspaceMembershipPermissions | null
): boolean {
  if (!permissions) return false;
  return (
    permissions.accessType === 'organization_admin' ||
    permissions.accessType === 'system_admin' ||
    permissions.role === 'system_admin'
  );
}

export function useWorkspacePermissions(workspaceId?: string) {
  const query = useQuery<WorkspaceMembershipPermissions>({
    queryKey: ['workspace-permissions', workspaceId],
    queryFn: async () => {
      const { data } = await api.get(`/workspaces/${workspaceId}/user-membership`);
      const raw = data?.data ?? data;
      return {
        role: raw?.role,
        accessType: raw?.accessType,
        canView: !!raw?.canView,
        canEdit: !!raw?.canEdit,
        canManage: !!raw?.canManage,
        canInvite: !!raw?.canInvite,
        canDelete: !!raw?.canDelete,
      };
    },
    enabled: !!workspaceId,
    staleTime: 60_000,
  });

  return { permissions: query.data ?? null, loading: query.isLoading };
}
